import * as React from 'react';

import {useDragDropManager} from '@dnd-kit/react';

import logger from 'src/utils/logger';
import {useCanvasContext} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/canvas/canvas-context';
import {MAX_TILES_PER_ROW} from 'src/components/analytics-genesis/byod/constants';
import {ROW_TYPE} from 'src/types/byod';
import {NEW_ROW_PREFIX} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/utils/handle-drag-end';
import {buildKeyboardStops} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/hooks/keyboard-stops';


type Direction = 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown';

function isDirectionKey(code: string): code is Direction {
  return (
    code === 'ArrowLeft' ||
    code === 'ArrowRight' ||
    code === 'ArrowUp' ||
    code === 'ArrowDown'
  );
}

function findDroppableElement(id: string): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const escaped = CSS.escape(id);
  // Sortable tiles carry data-byod-tile (value = tile id); new-row dividers
  // carry data-byod-droppable-id. Try tile first since same-row navigation
  // is the hot path.
  const tileEl = document.querySelector<HTMLElement>(
    `[data-byod-tile="${escaped}"]`,
  );
  if (tileEl) {
    return tileEl;
  }
  return document.querySelector<HTMLElement>(
    `[data-byod-droppable-id="${escaped}"]`,
  );
}

/**
 * Custom keyboard arrow handling for BYOD dashboard drag-and-drop.
 *
 * The default `@dnd-kit/dom` `KeyboardSensor` moves the source by a flat 10px
 * coordinate delta per arrow press, which (a) feels sluggish on tiles that are
 * 150–500px wide, and (b) lets repeated left/right presses walk the source
 * past a row's right edge so the next collision pick lands in a different
 * row. This hook installs a capture-phase listener that intercepts arrow
 * keys mid-drag and snaps the source to a discrete drop target instead:
 *
 *   - Left/Right: previous/next sortable in the **same row**. At a row
 *     boundary the press is swallowed (no escape to another row).
 *   - Up/Down: previous/next compatible row OR new-row drop band. New-row
 *     bands are interleaved between every row, so the user can also create
 *     a new row via keyboard.
 *
 * Pair with `KeyboardSensor.configure({ keyboardCodes: { up: [], down: [],
 * left: [], right: [] } })` on the provider so the default sensor doesn't
 * also handle these keys (which would double-fire moves).
 */
/**
 * Logical position of the keyboard cursor *within the keyboard-nav model* —
 * tracked in a ref instead of read from `target.sortable.index` because:
 *
 *   - With OptimisticSortingPlugin active, `sortable.index` is the
 *     post-shift logical index; if we feed that back into `row.tiles`
 *     (pre-drag order in the store) the indices diverge after several
 *     presses and arrow keys snap to the wrong tile.
 *   - The ref also lets us preserve the column index across up/down jumps
 *     between rows ("press down → land in same column"), which `sortable`
 *     can't tell us.
 *
 * Either `rowId + colIndex` is set (cursor inside a tile row) or
 * `newRowAfterIndex` is set (cursor on a new-row band) — never both.
 */
type LogicalPosition = {
  rowId: string | null;
  colIndex: number;
  newRowAfterIndex: number | null;
};

export function useByodKeyboardNav(): void {
  const manager = useDragDropManager();
  // Read the controlled layout from context; mirror in a ref so the keydown
  // handler (registered once per drag manager) always sees the latest.
  const {rowOrder, rows} = useCanvasContext();
  const stateRef = React.useRef({rowOrder, rows});
  stateRef.current = {rowOrder, rows};

  React.useEffect(() => {
    if (!manager || typeof document === 'undefined') {
      return undefined;
    }

    // Per-drag logical-position tracking. `lastSourceIdRef` is how we detect
    // a new drag (the user lifts a different tile) so we know to re-seed the
    // position from the current rows instead of carrying state across drags.
    const positionRef: {current: LogicalPosition | null} = {current: null};
    const lastSourceIdRef: {current: string | null} = {current: null};

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isDirectionKey(event.code)) {
        return;
      }
      const dragOperation = manager.dragOperation;
      // No drag in progress → leave keys for the rest of the page.
      if (dragOperation.status.idle) {
        return;
      }
      // We *always* claim arrow keys for the duration of a drag. Stop
      // immediate propagation so @dnd-kit's built-in `KeyboardSensor`
      // listener (also capture-phase on document, registered after ours)
      // can't run its own 10px-per-press handler in parallel — that's the
      // root cause of the "moves a little" behavior. Doing this even on
      // boundary no-ops keeps the page from scrolling mid-drag.
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      // The drag may still be in `initialization-pending` between the
      // Space-press handler completing and `dragging` taking effect; in
      // that window `actions.move` is a no-op, so we'd compute everything
      // and silently fail. Wait until dragging is fully active.
      if (!dragOperation.status.dragging) {
        return;
      }
      const source = dragOperation.source;
      if (!source) {
        return;
      }
      const rawData = source.data;
      const isValidObject = rawData != null && typeof rawData === 'object';
      const record = isValidObject
        ? (rawData as Record<string, unknown>)
        : undefined;
      const hasValidType =
        record !== undefined &&
        (typeof record.type === 'string' || record.type === undefined);
      const sourceData = hasValidType
        ? (record as {type?: string; rowId?: string})
        : undefined;
      const sourceType = sourceData?.type;
      const sourceRowId = sourceData?.rowId;
      if (
        !sourceRowId ||
        (sourceType !== 'kpi-tiles' && sourceType !== 'viz-tiles')
      ) {
        return;
      }

      const {rowOrder, rows} = stateRef.current;
      const variant: 'kpi' | 'viz' = sourceType === 'kpi-tiles' ? 'kpi' : 'viz';
      const expectedRowType = variant === 'kpi' ? ROW_TYPE.KPI : ROW_TYPE.VIZ;
      const maxTiles = MAX_TILES_PER_ROW[expectedRowType] ?? Infinity;
      const sourceId = String(source.id);

      // New drag (or first press of any drag) → seed position from the
      // source's slot in the current rows. After that, we own the position via
      // `positionRef` and never re-read it from collision-detection state.
      if (
        lastSourceIdRef.current !== sourceId ||
        positionRef.current === null
      ) {
        const fromRow = rows[sourceRowId];
        positionRef.current = {
          rowId: sourceRowId,
          colIndex: fromRow ? fromRow.tiles.indexOf(sourceId) : 0,
          newRowAfterIndex: null,
        };
        lastSourceIdRef.current = sourceId;
      }
      const position = positionRef.current;

      let nextDroppableId: string | null = null;

      if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') {
        // Same-row navigation only. From a new-row band there's no row to
        // navigate within, so left/right is a no-op (still swallowed so the
        // page doesn't scroll).
        if (position.rowId !== null) {
          const row = rows[position.rowId];
          if (row) {
            const dir = event.code === 'ArrowLeft' ? -1 : 1;
            const nextIdx = position.colIndex + dir;
            if (nextIdx >= 0 && nextIdx < row.tiles.length) {
              nextDroppableId = row.tiles[nextIdx];
              position.colIndex = nextIdx;
            }
          }
        }
      } else {
        // ArrowUp / ArrowDown: walk a flat list of valid drop "stops"
        // (compatible rows + new-row bands) in canvas order.
        const stops = buildKeyboardStops({
          rowOrder,
          rows,
          expectedRowType: variant,
          sourceRowId,
          maxTilesPerRow: maxTiles,
        });

        let cursor: number;
        if (position.newRowAfterIndex !== null) {
          cursor = stops.findIndex(
            (s) =>
              s.kind === 'new-row' &&
              s.afterIndex === position.newRowAfterIndex,
          );
        } else if (position.rowId) {
          cursor = stops.findIndex(
            (s) => s.kind === 'row' && s.rowId === position.rowId,
          );
        } else {
          cursor = -1;
        }
        // If the user's current row isn't in the stop list (e.g. it's now
        // ineligible), seed from the source row before navigating.
        if (cursor < 0) {
          cursor = stops.findIndex(
            (s) => s.kind === 'row' && s.rowId === sourceRowId,
          );
        }
        if (cursor < 0) {
          return;
        }

        const dir = event.code === 'ArrowUp' ? -1 : 1;
        const next = cursor + dir;
        if (next < 0 || next >= stops.length) {
          return;
        }
        const nextStop = stops[next];
        if (nextStop.kind === 'row') {
          // Preserve column when jumping rows: clamp the previous column
          // index into the next row's bounds. Lands on the closest tile
          // visually under the cursor, matching arrow-key behavior in
          // every other grid UI.
          const nextRow = rows[nextStop.rowId];
          const targetCol = Math.max(
            0,
            Math.min(position.colIndex, (nextRow?.tiles.length ?? 1) - 1),
          );
          nextDroppableId = nextRow?.tiles[targetCol] ?? null;
          position.rowId = nextStop.rowId;
          position.colIndex = targetCol;
          position.newRowAfterIndex = null;
        } else {
          nextDroppableId = `${NEW_ROW_PREFIX}${variant}-after-${nextStop.afterIndex}`;
          position.rowId = null;
          position.newRowAfterIndex = nextStop.afterIndex;
          // colIndex retained as-is so it still applies if the user lands
          // back on a tile row via the next ↑/↓ press.
        }
      }

      if (!nextDroppableId) {
        return;
      }

      const el = findDroppableElement(nextDroppableId);
      if (!el) {
        logger.warn('useByodKeyboardNav: droppable element not found', {
          nextDroppableId,
        });
        return;
      }
      const rect = el.getBoundingClientRect();
      // Move the source's position to the target droppable's center. The
      // collision observer reacts to the position update (via a microtask)
      // and re-picks the target — since we landed exactly on the center of
      // `nextDroppableId`, that droppable wins collision detection. We used
      // to also call `setDropTarget(nextDroppableId)` here as belt-and-
      // suspenders, but that races with the observer's recompute: the
      // observer's pick (after the microtask flushes) overwrites our
      // explicit set. Trusting collision detection avoids the race; if we
      // ever observe wrong indicator placement empirically, the fix is to
      // adjust collision priority on the affected droppables, not to fight
      // the observer from here.
      manager.actions.move({
        event,
        to: {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        },
        cancelable: false,
      });
    };

    document.addEventListener('keydown', onKeyDown, {capture: true});
    return () => {
      document.removeEventListener('keydown', onKeyDown, {capture: true});
    };
  }, [manager]);
}
